import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import { designTokens } from '@/utils/design-tokens'
import { AccountService } from '@/api/generated/services/AccountService'
import type { AccountResponse } from '@/api/generated/models/AccountResponse'
import type { AccountUpdateRequest } from '@/api/generated/models/AccountUpdateRequest'
import { useAlert } from '@/components/ui/Alert/exports'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { refreshUserData } from '@/store/authSlice'

interface AccountManagementModalProps {
  isOpen: boolean
  onClose: () => void
  user: AccountResponse | null
  onSuccess?: (updatedUser: AccountResponse) => void
}

export default function AccountManagementModal({
  isOpen,
  onClose,
  user,
  onSuccess
}: AccountManagementModalProps) {
  const { showError, showSuccess } = useAlert()
  const dispatch = useAppDispatch()
  const { selectedWorkspace } = useAppSelector(state => state.workspace)

  // Form state
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  // Form validation errors
  const [nameError, setNameError] = useState('')
  const [phoneError, setPhoneError] = useState('')

  // Initialize form with user data
  useEffect(() => {
    if (user && isOpen) {
      setName(user.name || '')
      setPhone(user.phone || '')
      setNameError('')
      setPhoneError('')
    }
  }, [user, isOpen])

  // Clear errors when user types
  useEffect(() => {
    setNameError('')
  }, [name])

  useEffect(() => {
    setPhoneError('')
  }, [phone])

  // Form validation
  const validateForm = () => {
    let isValid = true

    // Reset errors
    setNameError('')
    setPhoneError('')

    // Name validation
    if (!name.trim()) {
      setNameError('이름을 입력해주세요.')
      isValid = false
    } else if (name.trim().length < 2) {
      setNameError('이름은 2자 이상 입력해주세요.')
      isValid = false
    }

    // Phone validation (optional, but if provided must be valid)
    if (phone.trim()) {
      if (!/^01[0-9]-?[0-9]{3,4}-?[0-9]{4}$/.test(phone.replace(/\s/g, ''))) {
        setPhoneError('올바른 전화번호 형식을 입력해주세요. (예: 010-1234-5678)')
        isValid = false
      }
    }

    return isValid
  }

  const handleSubmit = async () => {
    if (!validateForm() || !user) {
      return
    }

    setIsLoading(true)

    try {
      const updateRequest: AccountUpdateRequest = {
        name: name.trim(),
        phone: phone.trim() || null
      }

      const updatedUser = await AccountService.appsAccountViewsUpdateAccount({
        accountId: user.id,
        requestBody: updateRequest
      })

      // Refresh user data in localStorage and Redux state
      await dispatch(refreshUserData(selectedWorkspace?.org_id))

      showSuccess('계정 정보가 성공적으로 수정되었습니다.')
      onSuccess?.(updatedUser)
      handleClose()
    } catch (error: any) {
      console.error('Failed to update account:', error)

      // Parse error data structure
      const errorData = error.body || error.response?.data || error
      const errorMessages: string[] = []

      // Extract detailed error messages
      if (errorData?.detail) {
        if (Array.isArray(errorData.detail)) {
          errorData.detail.forEach((item: any) => {
            if (item?.msg) {
              errorMessages.push(item.msg)
            }
          })
        } else if (typeof errorData.detail === 'string') {
          errorMessages.push(errorData.detail)
        }
      }

      if (errorData?.message) {
        errorMessages.push(errorData.message)
      }

      if (errorData?.error) {
        errorMessages.push(errorData.error)
      }

      // If we have specific error messages, use them
      if (errorMessages.length > 0) {
        showError(errorMessages.join(' / '))
      } else {
        // Fallback to status-based messages
        if (error && typeof error === 'object' && 'status' in error) {
          switch (error.status) {
            case 400:
              showError('입력 데이터가 올바르지 않습니다.')
              break
            case 401:
              showError('인증이 만료되었습니다. 다시 로그인해주세요.')
              break
            case 404:
              showError('계정을 찾을 수 없습니다.')
              break
            case 500:
              showError('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.')
              break
            default:
              showError('계정 정보 수정에 실패했습니다. 다시 시도해주세요.')
          }
        } else {
          showError('계정 정보 수정에 실패했습니다. 다시 시도해주세요.')
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    if (user) {
      setName(user.name || '')
      setPhone(user.phone || '')
    }
    setNameError('')
    setPhoneError('')
    onClose()
  }

  const isFormValid = name.trim().length >= 2

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      data-modal-overlay
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Background Blur Overlay */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}
        onClick={handleClose}
      />

      {/* Modal Content */}
      <div
        className="relative bg-white rounded w-[600px] max-w-[90vw] max-h-[90vh] overflow-auto border"
        style={{
          borderColor: designTokens.colors.gray[1]
        }}
      >
        <div className="flex flex-col gap-4 p-[30px]">
          {/* Header */}
          <div className="flex items-center justify-between gap-[10px] py-[7px]">
            <h2
              className="font-bold"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['title-medium'][0],
                lineHeight: designTokens.typography.fontSize['title-medium'][1].lineHeight,
                fontWeight: designTokens.typography.fontSize['title-medium'][1].fontWeight,
                color: designTokens.colors.gray[6]
              }}
            >
              계정 관리
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="w-6 h-6 flex items-center justify-center hover:opacity-70 transition-opacity"
              style={{ color: designTokens.colors.gray[5] }}
            >
              <X className="w-3 h-3" strokeWidth={2} />
            </button>
          </div>

          {/* Form Content */}
          <div className="space-y-6">
            {/* Email Field (Read-only) */}
            <div className="space-y-2">
              <label
                className="block font-normal"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: designTokens.typography.fontSize['body-medium'][0],
                  lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                  color: designTokens.colors.gray[6]
                }}
              >
                이메일
              </label>
              <Input
                type="email"
                value={user?.email || ''}
                readOnly
                disabled
                size="medium"
                className="w-full"
                style={{
                  backgroundColor: designTokens.colors.gray[0],
                  color: designTokens.colors.gray[4]
                }}
              />
              <p
                className="text-xs"
                style={{
                  color: designTokens.colors.gray[4],
                  fontFamily: designTokens.typography.fontFamily.sans[0]
                }}
              >
                이메일은 수정할 수 없습니다.
              </p>
            </div>

            {/* Name Field */}
            <div className="space-y-2">
              <label
                className="block font-normal"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: designTokens.typography.fontSize['body-medium'][0],
                  lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                  color: designTokens.colors.gray[6]
                }}
              >
                이름 <span style={{ color: designTokens.colors.error }}>*</span>
              </label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="이름을 입력해주세요."
                size="medium"
                state={nameError ? 'error' : 'default'}
                disabled={isLoading}
                className="w-full"
              />
              {nameError && (
                <p
                  className="text-sm"
                  style={{
                    color: designTokens.colors.error,
                    fontFamily: designTokens.typography.fontFamily.sans[0]
                  }}
                >
                  {nameError}
                </p>
              )}
            </div>

            {/* Phone Field */}
            <div className="space-y-2">
              <label
                className="block font-normal"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: designTokens.typography.fontSize['body-medium'][0],
                  lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                  color: designTokens.colors.gray[6]
                }}
              >
                전화번호
              </label>
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="전화번호를 입력해주세요. (예: 010-1234-5678)"
                size="medium"
                state={phoneError ? 'error' : 'default'}
                disabled={isLoading}
                className="w-full"
              />
              {phoneError && (
                <p
                  className="text-sm"
                  style={{
                    color: designTokens.colors.error,
                    fontFamily: designTokens.typography.fontFamily.sans[0]
                  }}
                >
                  {phoneError}
                </p>
              )}
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              size="medium"
              color="white"
              onClick={handleClose}
              disabled={isLoading}
            >
              취소
            </Button>
            <Button
              size="medium"
              color="dark"
              onClick={handleSubmit}
              disabled={isLoading || !isFormValid}
            >
              {isLoading ? '저장 중...' : '저장'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}